import {
    Box,
    Divider,
    Grid,
    Backdrop,
    CircularProgress,
} from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useTranslate } from "src/locales";
import { useEffect, useState } from "react";
import { referenceService } from "src/api/references/referenceService";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useAppSelector } from "src/app/hooks";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";



interface EvaluationEditModalProps {
    open: boolean;
    onClose: () => void;
    evalId: number | null;
    nrsId: string | undefined;
    patientId: any;
    onSave: () => void;
}

export const EvaluationEditModal = ({ open, onClose, evalId, nrsId, patientId, onSave }: EvaluationEditModalProps) => {
    const { t } = useTranslate();
    const { user } = useAppSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<any>({
        PROCESS_TIME: new Date(),
        NURSE_ID: "",
        ASSESSMENT_TREE_EQUAL_AND_BIG: 0,
        ASSESSMENT_TREE_THEN_SMALL: 0,
        DIETITIAN_NOTE: "",
        ASSESSMENT_NO: "",
    });

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState<"success" | "error">("success");

    useEffect(() => {
        const fetchEvalData = async () => {
            if (open && evalId) {
                setLoading(true);
                try {
                    const response = await referenceService.card.getCardTableData({
                        tableName: "MC_NUTRITIONAL_RISK_SCORE_ASS",
                        columnName: "ID",
                        columnValue: evalId
                    });
                    if (response?.data && response.data.length > 0) {
                        const record = response.data[0] as any;
                        setFormData({
                            ...record,
                            PROCESS_TIME: record.PROCESS_TIME ? new Date(record.PROCESS_TIME) : new Date(),
                        });
                    }
                } catch (error) {
                    console.error("Error fetching evaluation record:", error);
                } finally {
                    setLoading(false);
                }
            } else if (open && !evalId) {
                // Initialize for new record
                setFormData({
                    PROCESS_TIME: new Date(),
                    NURSE_ID: user?.id || "",
                    ASSESSMENT_TREE_EQUAL_AND_BIG: 0,
                    ASSESSMENT_TREE_THEN_SMALL: 0,
                    DIETITIAN_NOTE: "",
                    ASSESSMENT_NO: "",
                });
            }
        };

        fetchEvalData();
    }, [open, evalId]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const columns = Object.entries(formData)
                .filter(([key]) => !['ID', 'ROWID', 'CUSER', 'CTIME', 'NURSE_NAME', 'OVER_SEVENTY', 'CORE_VALUE'].includes(key))
                .map(([key, value]) => ({
                    key,
                    value: value instanceof Date ? value.toISOString() : String(value ?? ""),
                }));

            if (evalId) {
                await referenceService.card.put({
                    tablename: "MC_NUTRITIONAL_RISK_SCORE_ASS",
                    id: evalId,
                    columns: columns as any,
                });
            } else if (nrsId) {
                columns.push({ key: "NRSD_ID", value: nrsId });
                await referenceService.card.post({
                    tablename: "MC_NUTRITIONAL_RISK_SCORE_ASS",
                    columns: columns as any,
                });
            }
            onSave();
            setAlertMessage(t("card.record-updated-successfully"));
            setAlertType("success");
            setAlertOpen(true);
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (error) {
            console.error("Error saving evaluation:", error);
            setAlertMessage(t("card.update-failed"));
            setAlertType("error");
            setAlertOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <OpenComponentDialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            hideHeader
            showPatientData={false}
        >
            <Box
                sx={{
                    py: { xs: 2, sm: 3 },
                    px: { xs: 1.5, sm: 2 },
                    width: "100%",
                    maxWidth: "100%",
                }}
            >
                {/* Başlık */}
                <Box
                    sx={{
                        width: "100%",
                        backgroundColor: "#f4f7fe",
                        borderBottom: "1px solid #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        py: 1.5,
                        px: 2.5,
                        mb: 2,
                    }}
                >
                    <ByzMuiTypography isTitle={true} sx={{ fontSize: 20, color: "#111827" }}>
                        {t("nutritional-risk-score.edit-evaluation-title")}
                    </ByzMuiTypography>
                </Box>

                {/* Save / Close */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                        gap: 1,
                    }}
                >
                    <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
                    <Box sx={{ flex: 1 }} />
                    <ByzMuiCloseButton onClick={onClose} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Patient Info Banner */}
                <Box sx={{ mb: 2 }}>
                    <PatientDatas patientId={patientId} isCreateMedCard noMargin />
                </Box>

                {/* Form alanları */}
                <Box
                    sx={{
                        mt: 1,
                        width: { xs: "100%", md: "60%" },
                    }}
                >
                    {/* Bölüm başlığı */}
                    <Box
                        sx={{
                            backgroundColor: "#F4F7FE",
                            borderBottom: "1px solid #E0E0E0",
                            mb: 1.5,
                            px: 3,
                            py: 1,
                        }}
                    >
                        <ByzMuiTypography isTitle={true}>
                            {t("nutritional-risk-score.title")}
                        </ByzMuiTypography>
                    </Box>

                    <Grid container spacing={0}>
                        {/* Değerlendirme Tarihi */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {t("nutritional-risk-score.columns.evalation-date")}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzMuiDateTimePicker
                                        value={formData.PROCESS_TIME}
                                        onChange={(val) => handleChange("PROCESS_TIME", val)}
                                        width="100%"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Değerlendirme Veren Kullanıcı */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {t("nutritional-risk-score.columns.evalation-user")}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzMuiTextField
                                        fullWidth
                                        value={formData.CUSER || formData.NURSE_NAME || formData.NURSE_ID || ""}
                                        disabled
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* 70 Yaş Üstü */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {t("nutritional-risk-score.columns.seventy")}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzMuiCheckBox
                                        checked={Number(formData.OVER_SEVENTY) === 1}
                                        disabled
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Toplam Skor 3 ve Üzeri */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {t("nutritional-risk-score.columns.assessment-high")}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzMuiCheckBox
                                        checked={Number(formData.ASSESSMENT_TREE_EQUAL_AND_BIG) === 1}
                                        onChange={(e) => handleChange("ASSESSMENT_TREE_EQUAL_AND_BIG", e.target.checked ? 1 : 0)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Toplam Skor 3 Altı */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {t("nutritional-risk-score.columns.assessment-low")}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzMuiCheckBox
                                        checked={Number(formData.ASSESSMENT_TREE_THEN_SMALL) === 1}
                                        onChange={(e) => handleChange("ASSESSMENT_TREE_THEN_SMALL", e.target.checked ? 1 : 0)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Esas Değerlendirmeye Gerek Yok */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {t("nutritional-risk-score.columns.assessment-no")}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzMuiCheckBox
                                        checked={Number(formData.ASSESSMENT_NO) === 1}
                                        onChange={(e) => handleChange("ASSESSMENT_NO", e.target.checked ? 1 : 0)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Diyetisyen Notu */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {t("nutritional-risk-score.columns.note")}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzMuiTextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={formData.DIETITIAN_NOTE || ""}
                                        onChange={(e) => handleChange("DIETITIAN_NOTE", e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000, display: 'flex', flexDirection: 'column', gap: 2 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
                {loading && <ByzMuiTypography sx={{ color: '#fff' }}>{t("card.please-wait")}</ByzMuiTypography>}
            </Backdrop>

            <ByzMuiSnackBar
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
                message={alertMessage}
                type={alertType}
                title=""
                autoHideDuration={2000}
            />
        </OpenComponentDialog>
    );
};


